import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';
import API_BASE_URL from '../config';
import './HandheldInicio.css';

moment.locale('es');

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const REFRESH_INTERVAL = 60 * 1000; // 1 min

const HandheldInicio = () => {
  const token = useSelector((state) => state.auth.token);
  const navigate = useNavigate();

  const [servicios, setServicios] = useState([]);
  const [asignadosCount, setAsignadosCount] = useState(0);
  const [loading, setLoading] = useState(true);

  let userName = '';
  let noColaborador = '';
  let stationName = '';
  try {
    if (token) {
      const decoded = jwtDecode(token);
      userName = decoded.nombre || decoded.name || decoded.sub || '';
      noColaborador = decoded.noColaborador || decoded.id || '';
      stationName = decoded.estacion || decoded.station || '';
    }
  } catch (_) {}

  const todayFormatted = capitalize(moment().format('dddd D [de] MMMM'));
  const greeting = (() => {
    const h = moment().hour();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  })();

  const fetchData = useCallback(async () => {
    if (!token || !noColaborador) return;
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      const [svcRes, countRes] = await Promise.all([
        fetch(`${API_BASE_URL}api/servicios/colaborador/${noColaborador}`, { headers }),
        fetch(`${API_BASE_URL}api/servicios/asignados-hoy/${noColaborador}`, { headers }),
      ]);

      if (svcRes.ok) {
        const data = await svcRes.json();
        setServicios(Array.isArray(data) ? data : []);
      }

      if (countRes.ok) {
        const data = await countRes.json();
        setAsignadosCount(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching handheld data:', err);
    } finally {
      setLoading(false);
    }
  }, [token, noColaborador]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  const concluidos = servicios.filter((s) => s.statusServicio === 'CONCLUIDO').length;
  const noShow = servicios.filter((s) => s.statusServicio === 'NOSHOW').length;
  const totalHoy = servicios.length;

  const quickLinks = [
    { icon: '✈️', label: 'Llegadas', path: '/handheld/llegadas', color: '#3b82f6' },
    { icon: '🛫', label: 'Salidas', path: '/handheld/salidas', color: '#10b981' },
    { icon: '📝', label: 'Manual', path: '/handheld/manual', color: '#8b5cf6' },
    { icon: '⏳', label: 'Pendientes', path: '/handheld/pendientes', color: '#f59e0b' },
    { icon: '📖', label: 'Bitácora', path: '/handheld/bitacora', color: '#ec4899' },
    { icon: '👥', label: 'Gestión', path: '/handheld/gestion', color: '#06b6d4' },
  ];

  if (loading) {
    return (
      <div className="hh-container">
        <div className="hh-loading">
          <div className="hh-spinner" />
          <p className="hh-loading-text">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hh-container">
      {/* Greeting */}
      <div className="hh-greeting">
        <h1 className="hh-greeting-title">{greeting}{userName ? `, ${userName}` : ''}</h1>
        <p className="hh-greeting-sub">
          {stationName ? `${stationName} — ` : ''}{todayFormatted}
        </p>
      </div>

      {/* Stats */}
      <div className="hh-stats-row">
        <div className="hh-stat-card hh-stat-blue">
          <span className="hh-stat-value">{asignadosCount}</span>
          <span className="hh-stat-label">Asignados</span>
        </div>
        <div className="hh-stat-card hh-stat-green">
          <span className="hh-stat-value">{concluidos}</span>
          <span className="hh-stat-label">Concluidos</span>
        </div>
        <div className="hh-stat-card hh-stat-red">
          <span className="hh-stat-value">{noShow}</span>
          <span className="hh-stat-label">No Show</span>
        </div>
        <div className="hh-stat-card hh-stat-slate">
          <span className="hh-stat-value">{totalHoy}</span>
          <span className="hh-stat-label">Total Hoy</span>
        </div>
      </div>

      {/* Quick Access */}
      <div className="hh-section">
        <h2 className="hh-section-title">Acceso rápido</h2>
        <div className="hh-quick-grid">
          {quickLinks.map((link, i) => (
            <button
              key={i}
              className="hh-quick-btn"
              onClick={() => navigate(link.path)}
            >
              <div className="hh-quick-icon" style={{ background: `${link.color}22`, color: link.color }}>
                <span>{link.icon}</span>
              </div>
              <span className="hh-quick-label">{link.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent services */}
      {servicios.length > 0 && (
        <div className="hh-section">
          <h2 className="hh-section-title">Servicios recientes</h2>
          <div className="hh-services-list">
            {servicios.slice(0, 8).map((svc, i) => (
              <div key={svc._id || i} className="hh-service-item">
                <div className="hh-service-left">
                  <span className={`hh-service-status hh-status-${(svc.statusServicio || '').toLowerCase()}`}>
                    {svc.statusServicio || '—'}
                  </span>
                  <div className="hh-service-info">
                    <span className="hh-service-flight">{svc.noVuelo || 'Sin vuelo'}</span>
                    <span className="hh-service-detail">
                      {svc.tipoServicio || svc.tipo || ''} {svc.silla ? `• ${svc.silla}` : ''}
                    </span>
                  </div>
                </div>
                <span className="hh-service-time">
                  {svc.horaVuelo || svc.hora || ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HandheldInicio;
